/* ============================================================
   Elior — new cart drawer behaviour
   Three custom elements, all optional:
     <elior-cart-timer>      the yellow "Cart reserved for" bar
     <elior-cart-protection> the shipping-protection toggle
     <elior-cart-upsell>     the "+ Add" buttons under the item list

   The whole #MiniCart-<section> subtree is replaced on every cart
   update (see cart.js), so these elements are re-created constantly.
   Nothing here may keep state in the instance — the timer deadline
   lives in sessionStorage so it survives the re-render.
   ============================================================ */

(() => {
  const bundledSections = () => {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections } })
    );
    return sections;
  };

  const publishCart = (source, cart) => {
    theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source, cart });
  };

  /* The drawer header sits outside #MiniCart-<section>, so it is NOT replaced
     by the section refresh. This keeps the pluralised "3 items" label current. */
  if (!customElements.get('elior-cart-count')) {
    customElements.define(
      'elior-cart-count',
      class EliorCartCount extends HTMLElement {
        connectedCallback() {
          this.unsubscribe = theme.pubsub.subscribe(
            theme.pubsub.PUB_SUB_EVENTS.cartUpdate,
            this.onCartUpdate.bind(this)
          );
        }

        disconnectedCallback() {
          this.unsubscribe?.();
        }

        onCartUpdate(event) {
          if (event.cart?.errors) return;

          /* The protection line is hidden from the item list, so it must not be
             counted here either (the server-rendered label already skips it). */
          const exclude = this.getAttribute('data-exclude-variant');
          let count = event.cart.item_count;
          if (exclude) {
            const line = (event.cart.items || []).find(
              (item) => String(item.variant_id) === String(exclude)
            );
            if (line) count -= line.quantity;
          }
          const template = count === 1 ? theme.cartStrings.itemCountOne : theme.cartStrings.itemCountOther;
          this.textContent = template.replace('[count]', count);
          this.hidden = count === 0;
        }
      }
    );
  }

  if (!customElements.get('elior-cart-timer')) {
    customElements.define(
      'elior-cart-timer',
      class EliorCartTimer extends HTMLElement {
        connectedCallback() {
          this.output = this.querySelector('[data-time]');
          if (!this.output) return;

          const minutes = parseInt(this.getAttribute('data-minutes'), 10) || 10;
          this.storageKey = 'elior:cart-reserved-until';

          let deadline = parseInt(sessionStorage.getItem(this.storageKey) ?? '', 10);
          if (!deadline || Number.isNaN(deadline) || deadline <= Date.now()) {
            deadline = Date.now() + minutes * 60 * 1000;
            try {
              sessionStorage.setItem(this.storageKey, String(deadline));
            } catch (error) {
              // Private mode — the timer still runs, it just restarts on reload.
            }
          }
          this.deadline = deadline;

          this.tick();
          this.interval = setInterval(() => this.tick(), 1000);
        }

        disconnectedCallback() {
          clearInterval(this.interval);
        }

        tick() {
          const left = Math.max(0, this.deadline - Date.now());
          const total = Math.round(left / 1000);
          const mm = String(Math.floor(total / 60)).padStart(2, '0');
          const ss = String(total % 60).padStart(2, '0');
          this.output.textContent = `${mm}:${ss}`;

          if (left === 0) {
            clearInterval(this.interval);
            this.hidden = true;
          }
        }
      }
    );
  }

  if (!customElements.get('elior-cart-protection')) {
    customElements.define(
      'elior-cart-protection',
      class EliorCartProtection extends HTMLElement {
        static OPT_OUT_KEY = 'elior:protection-off';

        connectedCallback() {
          this.input = this.querySelector('input[type="checkbox"]');
          if (!this.input) return;

          this.variantId = this.getAttribute('data-variant-id');
          this.lineKey = this.getAttribute('data-line-key');

          this.onChange = this.onChange.bind(this);
          this.input.addEventListener('change', this.onChange);

          this.syncDefault();
        }

        disconnectedCallback() {
          this.input?.removeEventListener('change', this.onChange);
        }

        /* "Checked by default": the shopper never adds protection by hand, so as
           soon as the cart holds a real product we add it for them. The element is
           re-created on every cart refresh, so this runs again after each update —
           the data-line-key guard stops it from looping.
           A shopper who switches it off is remembered for the session, otherwise
           the next refresh would silently put it back. */
        syncDefault() {
          const otherItems = parseInt(this.getAttribute('data-other-items'), 10) || 0;
          const quantity = parseInt(this.getAttribute('data-line-quantity'), 10) || 0;
          const inCart = quantity > 0;

          // Last product removed — don't leave protection alone in the cart.
          if (inCart && otherItems === 0) {
            this.input.disabled = true;
            this.setQuantity(0);
            return;
          }

          // Self-heal: an older build could stack 2+ units on a double render.
          if (quantity > 1) {
            this.input.disabled = true;
            this.setQuantity(1);
            return;
          }

          if (this.getAttribute('data-default-on') !== 'true') return;
          if (inCart || otherItems === 0) return;
          if (EliorCartProtection.optedOut()) return;

          this.input.checked = true;
          this.input.disabled = true;
          this.setQuantity(1);
        }

        static optedOut() {
          try {
            return sessionStorage.getItem(EliorCartProtection.OPT_OUT_KEY) === '1';
          } catch (error) {
            return false; // private mode — just honour the default
          }
        }

        static rememberChoice(checked) {
          try {
            sessionStorage.setItem(EliorCartProtection.OPT_OUT_KEY, checked ? '0' : '1');
          } catch (error) {
            // private mode — the choice holds until the next refresh
          }
        }

        onChange() {
          this.input.disabled = true;
          EliorCartProtection.rememberChoice(this.input.checked);
          this.setQuantity(this.input.checked ? 1 : 0);
        }

        /* /cart/update sets an ABSOLUTE quantity for the variant, so two renders
           racing each other can no longer stack a second unit the way /cart/add
           did (that produced "Shipping Protection x2"). Quantity 0 removes it,
           and no line key is needed. */
        setQuantity(quantity) {
          const body = JSON.stringify({
            updates: { [this.variantId]: quantity },
            sections: bundledSections()
          });

          return fetch(theme.routes.cart_update_url, { ...theme.utils.fetchConfig(), body })
            .then((response) => response.json())
            .then((parsedState) => publishCart('elior-cart-protection', parsedState))
            .catch((error) => {
              console.error(error);
              this.input.checked = quantity === 0;
              this.input.disabled = false;
            });
        }
      }
    );
  }

  if (!customElements.get('elior-cart-upsell')) {
    customElements.define(
      'elior-cart-upsell',
      class EliorCartUpsell extends HTMLElement {
        connectedCallback() {
          this.onClick = this.onClick.bind(this);
          this.addEventListener('click', this.onClick);

          this.track = this.querySelector('.ecd-upsell__track');
          this.nav = this.querySelector('[data-nav]');
          this.prev = this.querySelector('[data-scroll="prev"]');
          this.next = this.querySelector('[data-scroll="next"]');

          if (this.track && this.nav) {
            this.syncArrows = this.syncArrows.bind(this);
            this.track.addEventListener('scroll', this.syncArrows, { passive: true });
            this.resizeObserver = new ResizeObserver(this.syncArrows);
            this.resizeObserver.observe(this.track);

            /* While the drawer carries [hidden] the track has no box at all,
               so the first measurement reads 0x0 and ResizeObserver stays
               quiet. Re-measure when the drawer's open state changes. */
            const drawer = this.closest('cart-drawer');
            if (drawer) {
              this.attributeObserver = new MutationObserver(this.syncArrows);
              this.attributeObserver.observe(drawer, {
                attributes: true,
                attributeFilter: ['hidden', 'open', 'active']
              });
            }

            this.syncArrows();
          }
        }

        disconnectedCallback() {
          this.removeEventListener('click', this.onClick);
          this.track?.removeEventListener('scroll', this.syncArrows);
          this.resizeObserver?.disconnect();
          this.attributeObserver?.disconnect();
        }

        /* One card plus the flex gap — so a click lands the next card
           against the track's padding rather than part-way across. */
        get step() {
          const card = this.track.firstElementChild;
          if (!card) return this.track.clientWidth;

          const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
          return card.getBoundingClientRect().width + gap;
        }

        syncArrows() {
          const max = this.track.scrollWidth - this.track.clientWidth;

          // Sub-pixel widths mean the ends never land on exact integers.
          this.nav.hidden = max <= 1;
          this.prev.disabled = this.track.scrollLeft <= 1;
          this.next.disabled = this.track.scrollLeft >= max - 1;
        }

        onClick(event) {
          const arrow = event.target.closest('[data-scroll]');
          if (arrow) {
            event.preventDefault();
            if (arrow.disabled) return;

            const direction = arrow.getAttribute('data-scroll') === 'next' ? 1 : -1;
            this.track.scrollBy({ left: this.step * direction, behavior: 'smooth' });
            return;
          }

          const button = event.target.closest('[data-variant-id]');
          if (!button || button.disabled) return;

          event.preventDefault();
          button.setAttribute('aria-busy', 'true');
          button.disabled = true;

          const body = JSON.stringify({
            items: [{ id: Number(button.getAttribute('data-variant-id')), quantity: 1 }],
            sections: bundledSections()
          });

          fetch(theme.routes.cart_add_url, { ...theme.utils.fetchConfig('javascript'), body })
            .then((response) => response.json())
            .then(async (parsedState) => {
              const cart = await (
                await fetch(theme.routes.cart_url, { ...theme.utils.fetchConfig('json', 'GET') })
              ).json();
              cart.sections = parsedState.sections;
              publishCart('elior-cart-upsell', cart);
            })
            .catch((error) => {
              console.error(error);
              button.removeAttribute('aria-busy');
              button.disabled = false;
            });
        }
      }
    );
  }
})();
